// Contabilidad › Analítico › ENVÍOS PROGRAMADOS.
// Manda un reporte de Inteligencia de negocios por correo cada mes / trimestre /
// año. Quien envía es el worker `notificador`, que arma el PDF y usa el SMTP
// PROPIO de Contabilidad (regla del proyecto: cada módulo con su correo).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Mail, Plus, Save, Send, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { field, rpc } from './shared';

interface ReporteItem { id: string; nombre: string; es_plantilla: boolean }
interface Envio {
  id: string; reporte_id: string; reporte: string; destinatarios: string[];
  periodicidad: string; dia: number; rango: string;
  cols_tipo: string; cols_param: string; con_presupuesto: boolean; activo: boolean;
  proximo_envio: string | null; ultimo_envio: string | null;
  ultimo_estado: string | null; ultimo_detalle: string | null;
}
interface Smtp {
  host?: string | null; puerto?: number | null; seguro?: boolean; usuario?: string | null;
  from_email?: string | null; from_nombre?: string | null; activo?: boolean;
  password_configurada?: boolean;
}

const PERIODICIDAD = [
  { v: 'mensual', label: 'Cada mes' },
  { v: 'trimestral', label: 'Cada trimestre' },
  { v: 'anual', label: 'Cada año' },
];
const RANGOS = [
  { v: 'mes_anterior', label: 'El mes anterior' },
  { v: 'trimestre_anterior', label: 'El trimestre anterior' },
  { v: 'anio_anterior', label: 'El año anterior' },
  { v: 'anio_en_curso', label: 'El año en curso hasta hoy' },
];
const PERIODOS = ['anual', 'semestral', 'trimestral', 'bimensual', 'mensual', 'ninguno'];

const vacio = {
  id: '', reporte_id: '', destinatarios: '', periodicidad: 'mensual', dia: 5,
  rango: 'mes_anterior', cols_param: 'mensual', con_presupuesto: false, activo: true,
};

// ---------------------------------------------------------------- SMTP -------
export function CardSmtp() {
  const qc = useQueryClient();
  const { data } = useQuery<Smtp>({ queryKey: ['cont-smtp'], queryFn: () => rpc<Smtp>('cont_smtp_get') });
  const [form, setForm] = useState<Record<string, string | boolean | number>>({});
  const val = (k: keyof Smtp, def: string | boolean | number = '') =>
    (form[k] !== undefined ? form[k] : (data?.[k] ?? def)) as string | boolean | number;
  const set = (k: string, v: string | boolean | number) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = useMutation({
    mutationFn: () => rpc('cont_smtp_set', {
      _data: {
        host: val('host'), puerto: Number(val('puerto', 587)), seguro: !!val('seguro', false),
        usuario: val('usuario'), from_email: val('from_email'), from_nombre: val('from_nombre'),
        activo: !!val('activo', true), password: (form.password as string) || '',
      },
    }),
    onSuccess: () => {
      toast.success('Correo del módulo guardado');
      setForm({});
      void qc.invalidateQueries({ queryKey: ['cont-smtp'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="h-4 w-4 text-primary" /> Correo saliente de Contabilidad
          {data?.password_configurada && (
            <span className="rounded bg-estable-soft px-1.5 py-0.5 text-[11px] font-normal text-estable">clave guardada</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Cada módulo usa su propio servidor de correo. Estas credenciales solo las usa el envío
          automático de reportes; la clave se guarda cifrada y no se vuelve a mostrar.
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex flex-col text-xs text-muted-foreground">Servidor (host)
            <input className={`${field} mt-1 w-56`} value={String(val('host'))} onChange={(e) => set('host', e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Puerto
            <input type="number" className={`${field} mt-1 w-24`} value={String(val('puerto', 587))} onChange={(e) => set('puerto', e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Usuario
            <input className={`${field} mt-1 w-56`} value={String(val('usuario'))} onChange={(e) => set('usuario', e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Clave
            <input type="password" className={`${field} mt-1 w-44`} placeholder={data?.password_configurada ? '•••••• (sin cambios)' : ''}
              value={String(form.password ?? '')} onChange={(e) => set('password', e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Remitente (correo)
            <input className={`${field} mt-1 w-56`} value={String(val('from_email'))} onChange={(e) => set('from_email', e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Remitente (nombre)
            <input className={`${field} mt-1 w-48`} value={String(val('from_nombre'))} onChange={(e) => set('from_nombre', e.target.value)} />
          </label>
          <label className="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={!!val('seguro', false)} onChange={(e) => set('seguro', e.target.checked)} />
            TLS directo (puerto 465)
          </label>
          <label className="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={!!val('activo', true)} onChange={(e) => set('activo', e.target.checked)} />
            Activo
          </label>
          <div className="ml-auto flex items-end pb-1">
            <Button size="sm" disabled={guardar.isPending} onClick={() => guardar.mutate()}>
              {guardar.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Guardar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------- ENVÍOS --------
export function BIEnvios() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [form, setForm] = useState<typeof vacio | null>(null);
  const set = (p: Partial<typeof vacio>) => setForm((f) => (f ? { ...f, ...p } : f));

  const { data: reportes = [] } = useQuery<ReporteItem[]>({
    queryKey: ['bi-reportes'], queryFn: () => rpc<ReporteItem[]>('cont_bi_reportes'),
  });
  const { data: envios = [], isFetching } = useQuery<Envio[]>({
    queryKey: ['bi-envios'], queryFn: () => rpc<Envio[]>('cont_bi_envios'),
  });

  const refrescar = () => void qc.invalidateQueries({ queryKey: ['bi-envios'] });

  const guardar = useMutation({
    mutationFn: () => rpc('cont_bi_envio_guardar', {
      _data: {
        id: form!.id || null, reporte_id: form!.reporte_id,
        destinatarios: form!.destinatarios.split(/[,;\s]+/).filter(Boolean),
        periodicidad: form!.periodicidad, dia: Number(form!.dia), rango: form!.rango,
        cols_tipo: 'tiempo', cols_param: form!.cols_param,
        con_presupuesto: form!.con_presupuesto, activo: form!.activo,
      },
    }),
    onSuccess: () => { toast.success('Programación guardada'); setForm(null); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => rpc('cont_bi_envio_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Envío eliminado'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const ahora = useMutation({
    mutationFn: (id: string) => rpc<{ nota: string }>('cont_bi_envio_ahora', { _id: id }),
    onSuccess: (r) => { toast.success(r.nota); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const editar = (e: Envio) => setForm({
    id: e.id, reporte_id: e.reporte_id, destinatarios: e.destinatarios.join(', '),
    periodicidad: e.periodicidad, dia: e.dia, rango: e.rango,
    cols_param: e.cols_param, con_presupuesto: e.con_presupuesto, activo: e.activo,
  });

  return (
    <div className="space-y-4 p-6">
      {dlg.ui}
      <div className="flex items-center gap-2">
        <Send className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-semibold">Envíos programados</h1>
      </div>

      <CardSmtp />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            Programación ({envios.length})
            {isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            <Button size="sm" variant="outline" className="ml-auto"
              onClick={() => setForm({ ...vacio, reporte_id: reportes[0]?.id ?? '' })}>
              <Plus className="mr-1 h-4 w-4" /> Nuevo envío
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {form && (
            <div className="space-y-3 rounded-md border bg-muted/40 p-3">
              <div className="flex flex-wrap gap-3">
                <label className="flex flex-col text-xs text-muted-foreground">Reporte
                  <select className={`${field} mt-1 w-64`} value={form.reporte_id}
                    onChange={(e) => set({ reporte_id: e.target.value })}>
                    <option value="">— elige —</option>
                    {reportes.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                  </select>
                </label>
                <label className="flex flex-col text-xs text-muted-foreground">Cada
                  <select className={`${field} mt-1 w-40`} value={form.periodicidad}
                    onChange={(e) => set({ periodicidad: e.target.value })}>
                    {PERIODICIDAD.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                  </select>
                </label>
                <label className="flex flex-col text-xs text-muted-foreground">Día
                  <input type="number" min={1} max={28} className={`${field} mt-1 w-20`} value={form.dia}
                    onChange={(e) => set({ dia: Number(e.target.value) })} />
                </label>
                <label className="flex flex-col text-xs text-muted-foreground">Contenido
                  <select className={`${field} mt-1 w-52`} value={form.rango}
                    onChange={(e) => set({ rango: e.target.value })}>
                    {RANGOS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                  </select>
                </label>
                <label className="flex flex-col text-xs text-muted-foreground">Columnas
                  <select className={`${field} mt-1 w-36`} value={form.cols_param}
                    onChange={(e) => set({ cols_param: e.target.value })}>
                    {PERIODOS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={form.con_presupuesto}
                    onChange={(e) => set({ con_presupuesto: e.target.checked })} />
                  Incluir presupuesto
                </label>
                <label className="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={form.activo}
                    onChange={(e) => set({ activo: e.target.checked })} />
                  Activo
                </label>
              </div>
              <label className="flex flex-col text-xs text-muted-foreground">Destinatarios (separados por coma)
                <input className={`${field} mt-1 w-full`} value={form.destinatarios}
                  placeholder="gerencia@empresa.com, contabilidad@empresa.com"
                  onChange={(e) => set({ destinatarios: e.target.value })} />
              </label>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                <Button size="sm" disabled={guardar.isPending || !form.reporte_id}
                  onClick={() => guardar.mutate()}>
                  {guardar.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                  Guardar
                </Button>
              </div>
            </div>
          )}

          {envios.length === 0 && !isFetching && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No hay envíos programados. Crea uno para que el reporte llegue solo por correo.
            </p>
          )}

          {envios.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-left">Reporte</th>
                  <th className="py-2 pr-3 text-left">Destinatarios</th>
                  <th className="py-2 pr-3 text-left">Cada</th>
                  <th className="py-2 pr-3 text-left">Próximo</th>
                  <th className="py-2 pr-3 text-left">Último</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {envios.map((e) => (
                  <tr key={e.id} className={`border-b last:border-0 ${e.activo ? '' : 'opacity-50'}`}>
                    <td className="py-1.5 pr-3">
                      <button className="underline decoration-dotted underline-offset-2" onClick={() => editar(e)}>
                        {e.reporte}
                      </button>
                      {e.con_presupuesto && <span className="ml-1 text-[10px] text-muted-foreground">+presup.</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-xs text-muted-foreground">{e.destinatarios.join(', ')}</td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-xs">
                      {PERIODICIDAD.find((p) => p.v === e.periodicidad)?.label} · día {e.dia}
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-xs">{e.proximo_envio ?? '—'}</td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-xs">
                      {e.ultimo_estado === 'enviado' && (
                        <span className="inline-flex items-center gap-1 text-estable" title={e.ultimo_detalle ?? ''}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> {e.ultimo_envio?.slice(0, 10)}
                        </span>
                      )}
                      {e.ultimo_estado === 'fallido' && (
                        <span className="inline-flex items-center gap-1 text-destructive" title={e.ultimo_detalle ?? ''}>
                          <XCircle className="h-3.5 w-3.5" /> falló
                        </span>
                      )}
                      {!e.ultimo_estado && <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-1.5 text-right">
                      <button className="mr-2 text-xs underline decoration-dotted"
                        title="Adelanta el envío al próximo ciclo del notificador"
                        onClick={() => ahora.mutate(e.id)}>Enviar ahora</button>
                      <button className="rounded p-1 text-destructive hover:bg-destructive/10" title="Eliminar"
                        onClick={async () => { if (await dlg.confirmar('¿Eliminar esta programación?', { peligro: true })) eliminar.mutate(e.id); }}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        El envío lo ejecuta el worker <em>notificador</em>, que revisa los pendientes en cada ciclo y adjunta
        el reporte en PDF. «Enviar ahora» no manda el correo en el acto: adelanta la fecha para que el worker
        lo tome en su siguiente pasada. Si un envío falla se anota el motivo y se reprograma para la siguiente
        fecha, no se reintenta en bucle.
      </p>
    </div>
  );
}
